export type FlyMateResponseData = {
  answer?: unknown;
  error?: unknown;
  code?: unknown;
  grounded?: unknown;
  reason?: unknown;
  requestId?: unknown;
  status?: unknown;
  payload?: unknown;
};

export class FlyMateRequestError extends Error {
  status: number | undefined;
  recoverable: boolean;

  constructor(message: string, options: { status?: number; recoverable?: boolean } = {}) {
    super(message);
    this.name = 'FlyMateRequestError';
    this.status = options.status;
    this.recoverable = options.recoverable === true;
  }
}

export const parseFlyMateResponse = (rawText: string): FlyMateResponseData | null => {
  try {
    const data = JSON.parse(rawText);
    return data && typeof data === 'object' && !Array.isArray(data) ? data : null;
  } catch {
    return null;
  }
};

const MAX_FLYMATE_RESPONSE_BYTES = 1024 * 1024;

export const readFlyMateResponse = async (
  response: Response,
  maxResponseBytes = MAX_FLYMATE_RESPONSE_BYTES,
): Promise<{ status: number; data: FlyMateResponseData | null }> => {
  const streaming = /text\/event-stream/i.test(response.headers.get('content-type') || '');
  if (!response.body) {
    if (streaming) throw new FlyMateRequestError('FlyMate disconnected before the answer arrived.', { recoverable: true });
    return { status: response.status, data: null };
  }
  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  let receivedBytes = 0;
  try {
    while (true) {
      const { value, done } = await reader.read();
      buffer += done ? decoder.decode() : decoder.decode(value, { stream: true });
      receivedBytes += value?.byteLength || 0;
      if (!streaming) {
        if (receivedBytes > maxResponseBytes) throw new FlyMateRequestError('FlyMate returned a response that was too large.', { recoverable: false });
        if (done) return { status: response.status, data: parseFlyMateResponse(buffer) };
        continue;
      }

      let separator = /\r?\n\r?\n/.exec(buffer);
      while (separator) {
        const frame = buffer.slice(0, separator.index);
        buffer = buffer.slice(separator.index + separator[0].length);
        if (new TextEncoder().encode(frame).byteLength > maxResponseBytes) {
          throw new FlyMateRequestError('FlyMate returned a response that was too large.', { recoverable: false });
        }
        let eventName = '';
        const dataLines: string[] = [];
        for (const line of frame.split(/\r?\n/)) {
          if (line.startsWith('event:')) eventName = line.slice(6).trim();
          if (line.startsWith('data:')) dataLines.push(line.slice(5).replace(/^ /, ''));
        }
        if (eventName === 'result') {
          const envelope = parseFlyMateResponse(dataLines.join('\n'));
          const status = envelope?.status;
          const payload = envelope?.payload;
          if (!Number.isInteger(status) || (status as number) < 100 || (status as number) > 599 ||
              !payload || typeof payload !== 'object' || Array.isArray(payload)) {
            throw new FlyMateRequestError('FlyMate returned an incomplete answer. Please try again.', { recoverable: true });
          }
          return { status: status as number, data: payload as FlyMateResponseData };
        }
        separator = /\r?\n\r?\n/.exec(buffer);
      }
      if (new TextEncoder().encode(buffer).byteLength > maxResponseBytes) {
        throw new FlyMateRequestError('FlyMate returned a response that was too large.', { recoverable: false });
      }
      if (done) throw new FlyMateRequestError('FlyMate disconnected before the answer arrived. Please try again.', { recoverable: true });
    }
  } catch (error) {
    if (error instanceof FlyMateRequestError) throw error;
    throw new FlyMateRequestError(
      isFlyMateTimeoutError(error)
        ? 'FlyMate took too long to respond. Please try again in a moment.'
        : 'FlyMate disconnected before the answer arrived. Please try again.',
      { recoverable: true },
    );
  } finally {
    await reader.cancel().catch(() => undefined);
    reader.releaseLock();
  }
};

export const hasFlyMateAnswer = (data: FlyMateResponseData | null): boolean =>
  typeof data?.answer === 'string' && Boolean(data.answer.trim()) &&
  !/<!doctype html|<html[\s>]/i.test(data.answer);

export const hasCurrentFlyMateRecoveredAnswer = (
  data: FlyMateResponseData | null,
  requestId: string,
): boolean => Boolean(requestId) && data?.requestId === requestId && hasFlyMateAnswer(data);

export const createFlyMateHttpError = (
  status: number,
  data: FlyMateResponseData | null,
  maxUploadFileSizeMb: number,
): FlyMateRequestError => {
  const apiError = typeof data?.error === 'string' && !/<!doctype html|<html[\s>]/i.test(data.error)
    ? data.error.trim().slice(0, 500)
    : '';
  const fallback = status === 413
    ? `The upload could not be accepted. FlyMate accepts up to ${maxUploadFileSizeMb} MB per file. Please try a smaller file.`
    : status === 504 || status === 408
      ? 'FlyMate took too long to respond. Please try again in a moment.'
      : status >= 500
        ? 'FlyMate is temporarily unavailable. Please try again in a moment.'
        : `FlyMate could not accept this request (${status}). Please try again.`;
  return new FlyMateRequestError(apiError || fallback, {
    status,
    recoverable: [408, 500, 502, 503, 504].includes(status),
  });
};

export const isFlyMateSessionError = (data: FlyMateResponseData | null): boolean =>
  data?.code === 'invalid_lead' ||
  (typeof data?.error === 'string' && /lead not found|introduction form|saved flymate session is no longer valid|invalid_lead|access has been blocked|verify your email/i.test(data.error));

export const shouldRecoverFlyMateRequest = (error: unknown): boolean =>
  error instanceof FlyMateRequestError ? error.recoverable : error instanceof TypeError || isFlyMateTimeoutError(error);

export const isFlyMateTimeoutError = (error: unknown): boolean =>
  error instanceof Error && ['TimeoutError', 'AbortError'].includes(error.name);

export const isFlyMateFailureReason = (reason: unknown): boolean =>
  typeof reason === 'string' && /^(?:timeout|[a-z0-9_]*_timeout|[a-z0-9_]*_unavailable|unavailable)$/i.test(reason);

export const shouldRetryFlyMateRecovery = (status: number, data: FlyMateResponseData | null): boolean =>
  status >= 500 || status === 408 || status === 429 ||
  (status >= 200 && status < 300 && !hasFlyMateAnswer(data)) ||
  (status === 404 && typeof data?.error === 'string' && /no recovered flymate answer .* yet/i.test(data.error));

export const flyMateMessageLabel = (message: {
  role: 'assistant' | 'user';
  grounded?: boolean;
  transportError?: boolean;
  reason?: string;
}): string => {
  if (message.role === 'user') return 'You';
  if (message.transportError || isFlyMateFailureReason(message.reason)) return 'FlyMate request failed';
  if (message.reason === 'missing_context') return 'FlyMate needs more details';
  if (message.reason === 'notam_draft') return 'FlyMate draft for review';
  return message.grounded ? 'FlyMate grounded answer' : 'FlyMate insufficient evidence';
};
